import {
  TravelItineraryEnum,
  type BookedFlightItinerary,
  type AirBookingFlightItinerarySlice,
  type TripContext,
} from "@b2bportal/air-booking-api";
import type { TripSlice } from "@b2bportal/air-shopping-api";
import { SliceDirection } from "@b2bportal/core-types";

export const getDepartureSlice = (
  bookedItinerary: BookedFlightItinerary
): AirBookingFlightItinerarySlice => {
  if (
    bookedItinerary.travelItinerary.TravelItinerary ===
    TravelItineraryEnum.SingleTravelItinerary
  ) {
    return bookedItinerary.travelItinerary.slices[0];
  }

  return bookedItinerary.travelItinerary.travelItineraries[0].slices[0];
};

export const getReturnSlice = (
  bookedItinerary: BookedFlightItinerary
): AirBookingFlightItinerarySlice | undefined => {
  let slices: AirBookingFlightItinerarySlice[];

  if (
    bookedItinerary.travelItinerary.TravelItinerary ===
    TravelItineraryEnum.SingleTravelItinerary
  ) {
    slices = bookedItinerary.travelItinerary.slices;
  } else {
    const itineraries = bookedItinerary.travelItinerary.travelItineraries;

    slices = itineraries[itineraries.length - 1].slices;
  }

  if (slices.length > 1) return slices[slices.length - 1];

  return undefined;
};

export const getSliceDepartureDate = (
  slice: AirBookingFlightItinerarySlice
) => {
  const { scheduledDeparture, updatedDeparture } = slice.segments[0];

  return updatedDeparture ?? scheduledDeparture;
};

export const getSliceDestinationCity = (
  slice: AirBookingFlightItinerarySlice,
  airports: TripContext["airports"]
) => {
  const lastSegment = slice.segments[slice.segments.length - 1];
  const sliceDestCode = lastSegment.destination.locationCode;

  return airports[sliceDestCode].cityName ?? sliceDestCode;
};

/**
 * Util to handle one-way, round-trip, and multi-city itineraries
 * @param targetSlice The slice to determine the direction of
 * @param allSlices The full list of slices in the itinerary
 * @example (A -> B, [A -> B, B -> A]) => SliceDirection.Outbound
 * @example (B -> A, [A -> B, B -> A]) => SliceDirection.Return
 * @example (A -> B, [A -> B, B -> C]) => SliceDirection.Outbound
 * @example (B -> C, [A -> B, B -> C]) => SliceDirection.Outbound
 */
export const getSliceDirection = (
  targetSlice: AirBookingFlightItinerarySlice,
  allSlices: AirBookingFlightItinerarySlice[]
) => {
  const targetSliceDestination =
    targetSlice.segments[targetSlice.segments.length - 1].destination
      .locationCode;
  const tripOrigin = allSlices[0].segments[0].origin.locationCode;

  return targetSliceDestination === tripOrigin
    ? SliceDirection.Return
    : SliceDirection.Outbound;
};

export const getSliceIndex = (
  departure: boolean,
  details: { slices: TripSlice[] }
): number => {
  return details?.slices.findIndex((slice) =>
    departure ? slice.outgoing : !slice.outgoing
  );
};

export const getSliceOriginCity = (
  slice: AirBookingFlightItinerarySlice,
  airports: TripContext["airports"]
) => {
  const lastSegment = slice.segments[0];
  const sliceOriginCode = lastSegment.origin.locationCode;

  return airports[sliceOriginCode].cityName ?? sliceOriginCode;
};
